// Gedeelde stukken van Koppelingen, met de markup van het prototype (Koppelingen.dc.html): de kaart met de lijst,
// een rij (links wat uit Planner of de mailbox komt, rechts waar het bij hoort, dan de pill en de knoppen), de kopjes
// ertussen, en een wijziging aan Clockify met Ongedaan maken in de melding.
import type { ReactNode } from "react";
import { clockifyApi, useClockifyWrite } from "../lib/api-clockify";
import { Icon } from "../lib/icons";
import { useToast } from "../shell/Toast";

export const OK = "ex-pill ex-pill--ok", WARN = "ex-pill ex-pill--warn", OFF = "ex-pill ex-pill--off";
/** zekerheid 0..1 als percentage */
export const pctOf = (score: number) => Math.round(score * 100);
/** toon van een zekerheid zoals in het prototype: vanaf 90 % groen, 80 tot 89 % oranje, lager grijs */
export const scorePill = (pct: number) => (pct >= 90 ? OK : pct >= 80 ? WARN : OFF);
/** Clockify-taken voor support zijn geen Planner-taak: hun uren tellen als supporturen (zoals SUPPORT in de server) */
export const isSupport = (name: string) => /support/i.test(name);

export interface KAct { label: string; primary?: boolean; title?: string; run: () => void }
export interface KRow {
  key: string;
  /** een kopje tussen de rijen in plaats van een rij */
  head?: string;
  cls?: string;
  a?: string; aSub?: string; aSubCls?: string;
  b?: string; bCls?: string; bSub?: string; bSubCls?: string;
  pill?: string; pillCls?: string;
  acts?: KAct[];
}

/** De kaart van een tabblad: titel en uitleg, rechts een actie, daaronder filters en een melding, dan de lijst. */
export function KoppelCard({ title, sub, tools, bar, notice, children, foot }: {
  title: string; sub: string; tools?: ReactNode; bar?: ReactNode; notice?: ReactNode; children: ReactNode; foot: string;
}) {
  return (
    <section className="p-card p-vis p-kp-main">
      <div className="p-card-h"><div><h2>{title}</h2><p>{sub}</p></div>{tools}</div>
      {bar}
      {notice}
      {children}
      <div className="p-card-f"><span>{foot}</span></div>
    </section>
  );
}

export function KoppelList({ rows, busy, empty }: { rows: KRow[]; busy: boolean; empty: string }) {
  return (
    <div className="p-kp-list">
      {rows.map((r) => (r.head ? <div key={r.key} className="p-kp-head">{r.head}</div> : (
        <div key={r.key} className={"p-kp-row" + (r.cls ? " " + r.cls : "")}>
          <div className="a"><b title={r.a}>{r.a}</b><span className={r.aSubCls} title={r.aSub}>{r.aSub}</span></div>
          <span className="p-kp-ar"><Icon name="arrow" size={15} /></span>
          <div className="b"><b className={r.bCls} title={r.b}>{r.b}</b><span className={r.bSubCls} title={r.bSub}>{r.bSub}</span></div>
          <span className="p-kp-pill">{r.pill && <span className={r.pillCls}>{r.pill}</span>}</span>
          <span className="p-kp-acts">
            {(r.acts || []).map((x) => (
              <button key={x.label} type="button" title={x.title} className={"ex-btn-xs" + (x.primary ? " ex-btn-xs--primary" : "")} disabled={busy} onClick={x.run}>{x.label}</button>
            ))}
          </span>
        </div>
      )))}
      {!rows.length && <p className="p-empty p-kp-empty">{empty}</p>}
    </div>
  );
}

/** Een melding over Clockify boven de lijst: nog niet gekoppeld of de laatste ronde mislukte. */
export function ClockifyNotice({ state, error }: { state: string; error: string }) {
  if (state === "uit") {
    return <div className="ex-notice ex-notice--mist p-notice p-kp-notice"><Icon name="info" size={16} /><p>Clockify is nog niet gekoppeld: de server heeft geen sleutel. Koppelingen bewaren kan, Clockify-taken maken nog niet.</p></div>;
  }
  if (state === "fout") {
    return <div className="ex-notice ex-notice--warning p-notice p-kp-notice"><Icon name="alert" size={16} /><p>De laatste lezing van Clockify mislukte: {error}. Projecten, taken en voorstellen kunnen verouderd zijn.</p></div>;
  }
  return null;
}

type Done = Awaited<ReturnType<typeof clockifyApi.linkTasks>>;

/** Wijziging aan Clockify-koppelingen: uitvoeren, alles met uren opnieuw laden, en Ongedaan maken met de tegenzet. */
export function useKoppelSave() {
  const w = useClockifyWrite();
  const toast = useToast();
  const save = (run: () => Promise<Done>, undo?: () => Promise<Done>): Promise<boolean> =>
    w.run(run).then((r) => {
      if (undo) toast.flash(r.text, { undo: () => { w.run(undo).catch(() => {}); } });
      return true;
    }, () => false);
  return { save, busy: w.busy };
}
